import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { selectUser, logout } from '../../store/slices/authSlice';

interface NavbarProps {
  title?: string;
}

const Navbar: React.FC<NavbarProps> = ({ title = 'WhatsApp Business System' }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setProfileDropdownOpen(false);
      }
    };
    
    if (profileDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [profileDropdownOpen]);
  
  // Handle logout
  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };
  return (
    <nav className="bg-whatsapp-teal text-white shadow-md">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo and title */}
          <div className="flex items-center">
            <img src="/logo/favicon-32x32.png" alt="Logo" className="h-8 w-8 mr-2" />
            <span className="text-xl font-bold">{title}</span>
          </div>
          
          {/* Navigation Links - For larger screens */}
          <div className="hidden md:block">
            <div className="ml-10 flex items-baseline space-x-4">
              <Link to="/dashboard" className="px-3 py-2 rounded-md text-sm font-medium hover:bg-whatsapp-dark-green">Dashboard</Link>
              <Link to="/messages" className="px-3 py-2 rounded-md text-sm font-medium hover:bg-whatsapp-dark-green">Messages</Link>
              <Link to="/contacts" className="px-3 py-2 rounded-md text-sm font-medium hover:bg-whatsapp-dark-green">Contacts</Link>
              <Link to="/templates" className="px-3 py-2 rounded-md text-sm font-medium hover:bg-whatsapp-dark-green">Templates</Link>
              <Link to="/campaigns" className="px-3 py-2 rounded-md text-sm font-medium hover:bg-whatsapp-dark-green">Campaigns</Link>
              <Link to="/analytics" className="px-3 py-2 rounded-md text-sm font-medium hover:bg-whatsapp-dark-green">Analytics</Link>
            </div>
          </div>
          
          {/* User profile dropdown */}
          <div className="ml-3 relative" ref={dropdownRef}>
            <div>
              <button 
                onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
                type="button" 
                className="flex items-center max-w-xs bg-whatsapp-green rounded-full p-1 focus:outline-none"
              >
                <span className="sr-only">Open user menu</span>
                <div className="h-8 w-8 rounded-full bg-whatsapp-dark-green flex items-center justify-center text-white">
                  <span className="text-lg font-semibold">{user?.name?.charAt(0).toUpperCase() || 'U'}</span>
                </div>
              </button>
            </div>
            
            {/* Profile dropdown panel */}
            {profileDropdownOpen && (
              <div className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 z-50">
                <div className="px-4 py-2 border-b border-gray-100">
                  <p className="text-sm font-medium text-gray-900">{user?.name || 'User'}</p>
                  <p className="text-xs text-gray-500">{user?.email || 'user@example.com'}</p>
                </div>
                <Link 
                  to="/settings" 
                  className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  onClick={() => setProfileDropdownOpen(false)}
                >
                  Settings
                </Link>
                <button
                  onClick={() => {
                    setProfileDropdownOpen(false);
                    handleLogout();
                  }}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-red-600"
                >
                  Sign out
                </button>
              </div>
            )}
          </div>
          
          {/* Mobile menu button */}
          <div className="-mr-2 flex md:hidden">
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              type="button" 
              className="inline-flex items-center justify-center p-2 rounded-md text-white hover:bg-whatsapp-dark-green focus:outline-none"
              aria-expanded="false"
            >
              <span className="sr-only">Open main menu</span>
              {mobileMenuOpen ? (
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
                </svg>
              )}
            </button>
          </div>
        </div>
        
        {/* Mobile menu, show/hide based on menu state */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-whatsapp-teal border-t border-whatsapp-dark-green">
            <div className="px-2 pt-2 pb-3 space-y-1">
              <Link 
                to="/dashboard" 
                className={`block px-3 py-2 rounded-md text-base font-medium ${location.pathname === '/dashboard' ? 'bg-whatsapp-dark-green text-white' : 'text-white hover:bg-whatsapp-dark-green'}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                Dashboard
              </Link>
              <Link 
                to="/messages" 
                className={`block px-3 py-2 rounded-md text-base font-medium ${location.pathname.includes('/messages') ? 'bg-whatsapp-dark-green text-white' : 'text-white hover:bg-whatsapp-dark-green'}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                Messages
              </Link>
              <Link 
                to="/contacts" 
                className={`block px-3 py-2 rounded-md text-base font-medium ${location.pathname === '/contacts' ? 'bg-whatsapp-dark-green text-white' : 'text-white hover:bg-whatsapp-dark-green'}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                Contacts
              </Link>
              <Link 
                to="/templates" 
                className={`block px-3 py-2 rounded-md text-base font-medium ${location.pathname.includes('/templates') ? 'bg-whatsapp-dark-green text-white' : 'text-white hover:bg-whatsapp-dark-green'}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                Templates
              </Link>
              <Link 
                to="/campaigns" 
                className={`block px-3 py-2 rounded-md text-base font-medium ${location.pathname.includes('/campaigns') ? 'bg-whatsapp-dark-green text-white' : 'text-white hover:bg-whatsapp-dark-green'}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                Campaigns
              </Link>
              <Link 
                to="/analytics" 
                className={`block px-3 py-2 rounded-md text-base font-medium ${location.pathname === '/analytics' ? 'bg-whatsapp-dark-green text-white' : 'text-white hover:bg-whatsapp-dark-green'}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                Analytics
              </Link>
              <Link 
                to="/phones" 
                className={`block px-3 py-2 rounded-md text-base font-medium ${location.pathname === '/phones' ? 'bg-whatsapp-dark-green text-white' : 'text-white hover:bg-whatsapp-dark-green'}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                Phones
              </Link>
              <Link 
                to="/settings" 
                className={`block px-3 py-2 rounded-md text-base font-medium ${location.pathname === '/settings' ? 'bg-whatsapp-dark-green text-white' : 'text-white hover:bg-whatsapp-dark-green'}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                Settings
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
